import {
  buildSiteConfigForLocation,
  resolveLocationByHost,
  type PlatformLocation,
} from "@/lib/restaurant-platform";

export type AnalyticsBrandId = string;

export type AnalyticsBrandConfig = {
  brandId: AnalyticsBrandId;
  restaurantId: string;
  locationId: string;
  hostname: string;
  isProductionHost: boolean;
  ga4Id: string;
  posthogKey: string;
  metaPixelId: string;
  appName: string;
  storage: {
    posthogDistinctId: string;
    utmParams: string;
    ga4Attribution: string;
    latestAttribution: string;
    firstTouchAttribution: string;
    sessionId: string;
  };
};

const CAPTURE_LOCALHOST =
  process.env.NEXT_PUBLIC_ANALYTICS_CAPTURE_LOCALHOST === "true" ||
  process.env.NEXT_PUBLIC_GA4_CAPTURE_LOCALHOST === "true" ||
  process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_LOCALHOST === "true";

const CAPTURE_PREVIEW =
  process.env.NEXT_PUBLIC_ANALYTICS_CAPTURE_PREVIEW === "true" ||
  process.env.NEXT_PUBLIC_GA4_CAPTURE_PREVIEW === "true" ||
  process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_PREVIEW === "true";

const BRAND_APP_NAMES: Record<string, string> = {
  "tuscan-cove": "tuscan_cove_bar_patio",
  "roma-kitchen": "chefs_roma_kitchen",
  "fia-restaurant": "fia_restaurant",
};

const BRAND_STORAGE_PREFIX: Record<string, string> = {
  "tuscan-cove": "tuscan_cove",
  "roma-kitchen": "roma_kitchen",
  "fia-restaurant": "fia_restaurant",
};

const LEGACY_ROMA_UTM_STORAGE_KEY = "roma_utm_params";

function normalizeHostname(input: string | undefined): string {
  if (!input) return "";

  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return "";

  try {
    const url = trimmed.includes("://") ? new URL(trimmed) : new URL(`https://${trimmed}`);
    return url.hostname.replace(/^www\./, "");
  } catch {
    return trimmed
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0]
      .split(":")[0];
  }
}

function isLocalHost(hostname: string) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function isPreviewHost(hostname: string) {
  return hostname.endsWith(".vercel.app");
}

function resolveGa4Id(restaurantId: string): string {
  const env = process.env;

  if (restaurantId === "roma-kitchen") {
    return env.NEXT_PUBLIC_ROMA_GA4_ID?.trim() ?? "";
  }

  if (restaurantId === "fia-restaurant") {
    return env.NEXT_PUBLIC_FIA_GA4_ID?.trim() ?? "";
  }

  if (restaurantId === "tuscan-cove") {
    return (
      env.NEXT_PUBLIC_TUSCAN_GA4_ID?.trim() ??
      env.NEXT_PUBLIC_GA4_ID?.trim() ??
      env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim() ??
      ""
    );
  }

  return "";
}

function resolvePosthogKey(restaurantId: string): string {
  const env = process.env;

  if (restaurantId === "roma-kitchen") {
    return (
      env.NEXT_PUBLIC_ROMA_POSTHOG_KEY?.trim() ??
      env.NEXT_PUBLIC_ROMA_POSTHOG_PROJECT_TOKEN?.trim() ??
      ""
    );
  }

  if (restaurantId === "fia-restaurant") {
    return (
      env.NEXT_PUBLIC_FIA_POSTHOG_KEY?.trim() ??
      env.NEXT_PUBLIC_FIA_POSTHOG_PROJECT_TOKEN?.trim() ??
      ""
    );
  }

  if (restaurantId === "tuscan-cove") {
    return (
      env.NEXT_PUBLIC_TUSCAN_POSTHOG_KEY?.trim() ??
      env.NEXT_PUBLIC_POSTHOG_KEY?.trim() ??
      env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN?.trim() ??
      ""
    );
  }

  return "";
}

function buildStorageKeys(brandId: AnalyticsBrandId) {
  const prefix =
    BRAND_STORAGE_PREFIX[brandId] ??
    brandId.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase();

  return {
    posthogDistinctId: `${prefix}_posthog_distinct_id`,
    utmParams: `${prefix}_utm_params`,
    ga4Attribution: `${prefix}_ga4_attribution`,
    latestAttribution: `${prefix}_latest_attribution`,
    firstTouchAttribution: `${prefix}_first_touch_attribution`,
    sessionId: `${prefix}_analytics_session_id`,
  };
}

function isBrandProductionHost(hostname: string, location: PlatformLocation): boolean {
  const normalizedHost = normalizeHostname(hostname);
  const brandHosts = location.domains.map((domain) => normalizeHostname(domain));

  if (!brandHosts.includes(normalizedHost)) {
    return false;
  }

  if (isPreviewHost(normalizedHost)) {
    return CAPTURE_PREVIEW;
  }

  return true;
}

export function getBrowserHostname(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location.hostname;
}

export function resolveAnalyticsBrandConfig(
  host?: string,
): AnalyticsBrandConfig | null {
  const hostname = normalizeHostname(host ?? getBrowserHostname());
  if (!hostname) return null;

  const location = resolveLocationByHost(hostname);
  const brandId = location.restaurantId;
  const siteConfig = buildSiteConfigForLocation(location);

  return {
    brandId,
    restaurantId: location.restaurantId,
    locationId: location.id,
    hostname,
    isProductionHost: isBrandProductionHost(hostname, location),
    ga4Id: resolveGa4Id(location.restaurantId),
    posthogKey: resolvePosthogKey(location.restaurantId),
    metaPixelId: siteConfig.metaPixelId,
    appName: BRAND_APP_NAMES[brandId] ?? brandId.replace(/-/g, "_"),
    storage: buildStorageKeys(brandId),
  };
}

export function shouldCaptureAnalytics(host?: string): boolean {
  const config = resolveAnalyticsBrandConfig(host);
  if (!config) return false;

  if (config.isProductionHost) {
    return true;
  }

  if (isLocalHost(config.hostname)) {
    return CAPTURE_LOCALHOST;
  }

  return false;
}

export function getAnalyticsBrandProperties(
  config: AnalyticsBrandConfig,
): Record<string, string> {
  return {
    analytics_brand: config.brandId,
    restaurant_id: config.restaurantId,
    location_id: config.locationId,
    site_hostname: config.hostname,
    app_name: config.appName,
  };
}

export function readBrandUtmStorage(config: AnalyticsBrandConfig): string | null {
  if (typeof window === "undefined") return null;

  try {
    return (
      window.sessionStorage.getItem(config.storage.utmParams) ??
      (config.brandId === "roma-kitchen"
        ? window.sessionStorage.getItem(LEGACY_ROMA_UTM_STORAGE_KEY)
        : null)
    );
  } catch {
    return null;
  }
}

export function writeBrandUtmStorage(
  config: AnalyticsBrandConfig,
  value: Record<string, string>,
) {
  if (typeof window === "undefined") return;

  try {
    const serialized = JSON.stringify(value);
    window.sessionStorage.setItem(config.storage.utmParams, serialized);
    if (config.brandId === "roma-kitchen") {
      window.sessionStorage.setItem(LEGACY_ROMA_UTM_STORAGE_KEY, serialized);
    }
  } catch {
    // sessionStorage may be blocked.
  }
}

export function resolveMetaPixelIdFromHost(host?: string): string {
  const hostname = normalizeHostname(host ?? getBrowserHostname());
  if (!hostname) return "";

  const location = resolveLocationByHost(hostname);
  return buildSiteConfigForLocation(location).metaPixelId;
}
